import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { BancoldexService } from 'src/app/comun/services/bancoldex.service';
import { HOME_CONTENT_MOCK } from '../data/home-content.mock';
import type {
  HomeTestimonioItem,
  HomeTestimoniosBannersActivosResponse,
  HomeTestimoniosBannerRow,
  HomeTestimoniosContent,
} from '../models/home-testimonios-content.model';
import { mapTestimoniosBannersToContent } from '../utils/home-testimonios-banner.mapper';

/** Activos Video + VideoEncuesta (mismo endpoint / query backend). */
export const TESTIMONIOS_BANNER_TIPO = 'Video';

@Injectable({
  providedIn: 'root',
})
export class HomeTestimoniosService {
  private readonly fallbackContent: HomeTestimoniosContent =
    buildTestimoniosFallbackFromMock();

  constructor(private readonly bancoldexService: BancoldexService) {}

  loadContent(): Observable<HomeTestimoniosContent> {
    return this.bancoldexService
      .getArchivosBannerActivos(TESTIMONIOS_BANNER_TIPO)
      .pipe(
        map((response: HomeTestimoniosBannersActivosResponse) => {
          const rows = normalizeTestimoniosBannerRows(response);
          if (!rows.length) {
            return this.fallbackContent;
          }
          return mapTestimoniosBannersToContent(rows, this.fallbackContent);
        }),
        catchError(() => of(this.fallbackContent))
      );
  }
}

function buildTestimoniosFallbackFromMock(): HomeTestimoniosContent {
  const mock = HOME_CONTENT_MOCK.testimonios;
  return {
    eyebrow: mock.eyebrow,
    title: mock.title,
    items: (mock.items ?? []).map(
      (item, index): HomeTestimonioItem => ({
        idBanner: null,
        orden: index + 1,
        name: item.name,
        role: item.role,
        quote: item.quote,
        tipoBanner: 'Video',
        embedUrl: null,
        thumbnailUrl: item.image,
        mediaUrl: null,
        imageAlt: item.imageAlt,
      })
    ),
  };
}

function normalizeTestimoniosBannerRows(
  response: HomeTestimoniosBannersActivosResponse
): HomeTestimoniosBannerRow[] {
  if (!response?.exitoso || !response.banners?.length) {
    return [];
  }
  const first = response.banners[0] as unknown;
  if (Array.isArray(first)) {
    return (response.banners as HomeTestimoniosBannerRow[][]).flat();
  }
  return response.banners as HomeTestimoniosBannerRow[];
}
